/**
 * An agent's Runs: its recent work (Work Items, newest first), each with the steps that ran on
 * it by firing, what got linked (a ticket, a Slack thread), what people approved, and how its newest
 * firing went (outcome.ts). Watch (Logs once that's done, Review while a hand-off waits) opens one
 * live (WorkItemSheet): any pending approval to decide, then each step's run as it goes, following
 * the work through hand-offs.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ApiService, { ApiError } from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { useUserRoles } from '@/hooks/useUserRoles';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import type { Member } from './catalog';
import { RunDot, RunLive, Spinner } from './RunView';
import { useRunStatuses } from './data';
import { isDone, workOutcome, type Outcome } from './outcome';

type Link_ = { name: string; key: string | null; kind: string; title: string | null; url: string | null };
// `payload`, only while pending: exactly what approving hands on (the step's submitted output).
type Approval = {
  id: string;
  status: string;
  from_agent: string;
  to_agent: string;
  payload?: { summary?: string; fields?: Record<string, unknown> };
  run_id: string | null;
  decided_by: string | null;
  changes: string | null;
  created_at: string;
};
// One delivery that started work (an alert firing, a request) and the runs that followed from it.
type Firing = { at: string | null; runs: string[] };
// A step that couldn't start: the harness refused it, it had no grant, or the harness was down.
// `run`: for a failed hand-off, the run that handed on.
type Failure = { event: string | null; reason: string; run: string | null; at: string };
type WorkItem = {
  id: string;
  title: string;
  status: string;
  links: Link_[];
  runs: string[];
  firings: Firing[];
  approvals: Approval[];
  failures: Failure[];
  created_at: string;
  updated_at: string;
};

const OUTCOME_TONE: Record<Outcome, string> = {
  Running: '',
  'Waiting for approval': 'text-amber-600',
  Success: 'text-green-600',
  Failed: 'text-destructive',
};

function OutcomeBadge({ outcome }: { outcome: Outcome | null }) {
  return outcome ? (
    <Badge variant="outline" className={OUTCOME_TONE[outcome]}>
      {outcome}
    </Badge>
  ) : null;
}

const APPROVAL: Record<string, string> = { pending: 'Waiting for approval', approved: 'Approved', rejected: 'Rejected' };

// Keep in step with the Slack modal's hint (approvals.CHANGES_HINT).
const CHANGES_HINT =
  'Your changes go to the next step as instructions, alongside what was approved. Its agent applies them itself, so they aren’t guaranteed: check its run.';

/** "Approved by x: …", as the card and a decided panel show it. */
function ApprovalLine({ a, children }: { a: Approval; children?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span>
        <span className={a.status === 'pending' ? 'text-amber-600' : 'text-muted-foreground'}>{APPROVAL[a.status] ?? a.status}</span>
        {a.decided_by ? ` by ${a.decided_by}` : ''}
        {a.changes ? <span className="text-muted-foreground">: “{a.changes}”</span> : null}
      </span>
      {children}
    </div>
  );
}

/** A pending hand-off: what approving hands on, and (for an Admin, as in Slack) Approve, Approve
 * with changes or Reject. The first decision wins, here or in Slack. */
function ApprovalPanel({ teamId, a, name }: { teamId: string; a: Approval; name: (agentId: string) => string }) {
  const { isAdmin } = useUserRoles();
  const qc = useQueryClient();
  const [changes, setChanges] = useState<string | null>(null); // open: "Approve with changes"
  const decide = useMutation({
    mutationFn: async (body: { decision: 'approve' | 'reject'; changes?: string }) =>
      (await ApiService.getInstance().request('POST', `/agent-teams/${teamId}/approvals/${a.id}`, body)) as { error: string | null },
    // Decided (or already decided, 409) either way: refetch to show who decided.
    onSettled: () => qc.invalidateQueries({ queryKey: ['agent-teams'] }),
  });
  const error =
    decide.error instanceof ApiError
      ? ((decide.error.payload as unknown as { error?: string } | undefined)?.error ?? 'Couldn’t record the decision.')
      : decide.error
        ? 'Couldn’t record the decision.'
        : decide.data?.error
          ? `Approved, but the next step couldn’t start: ${decide.data.error}`
          : null;
  const fields = Object.entries(a.payload?.fields ?? {});
  return (
    <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50/50 p-3 dark:bg-amber-950/20">
      <p className="text-sm font-medium">
        {name(a.from_agent)} → {name(a.to_agent)}: waiting for approval
      </p>
      <div className="max-h-64 overflow-auto rounded border bg-background p-2 text-sm whitespace-pre-wrap">
        {a.payload?.summary || <span className="text-muted-foreground">(no summary)</span>}
      </div>
      {fields.length > 0 && (
        <p className="text-xs text-muted-foreground">{fields.map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join(' · ')}</p>
      )}
      {!isAdmin ? (
        <p className="text-xs text-muted-foreground">Waiting for an Admin.</p>
      ) : changes !== null ? (
        <div className="space-y-2">
          <Textarea
            autoFocus
            value={changes}
            placeholder="What should change?"
            aria-label="What should change?"
            aria-describedby={`changes-hint-${a.id}`}
            onChange={(e) => setChanges(e.target.value)}
          />
          <p id={`changes-hint-${a.id}`} className="text-xs text-muted-foreground">
            {CHANGES_HINT}
          </p>
          <div className="flex gap-2">
            <Button size="sm" disabled={decide.isPending || !changes.trim()} onClick={() => decide.mutate({ decision: 'approve', changes })}>
              Approve
            </Button>
            <Button size="sm" variant="ghost" disabled={decide.isPending} onClick={() => setChanges(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ decision: 'approve' })}>
            Approve
          </Button>
          <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => setChanges('')}>
            Approve with changes
          </Button>
          <Button size="sm" variant="destructive" disabled={decide.isPending} onClick={() => decide.mutate({ decision: 'reject' })}>
            Reject
          </Button>
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function Failures({ failures }: { failures: Failure[] }) {
  return failures.map((f) => (
    <p key={f.at + f.reason} className="text-xs text-destructive">
      {f.event === 'output.submitted' ? 'A hand-off couldn’t start the next step' : 'Couldn’t start'} ({new Date(f.at).toLocaleTimeString()}): {f.reason}
    </p>
  ));
}

/** A Work Item collects every repeat of its alert, so its runs show one row per firing, newest
 * first, with hand-offs (and approved hand-offs) in order within it. */
function Firings({ firings, run }: { firings: Firing[]; run: (runId: string) => React.ReactNode }) {
  return (
    <div className="space-y-1">
      {[...firings].reverse().map((f) => (
        <div key={f.runs[0]} className="flex flex-wrap items-center gap-1">
          <span className="w-20 text-xs text-muted-foreground">{f.at ? new Date(f.at).toLocaleTimeString() : ''}</span>
          {f.runs.map((r, i) => (
            <span key={r} className="flex items-center gap-1">
              {i > 0 && <span className="text-muted-foreground">→</span>}
              {run(r)}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function useWorkItems(teamId: string, refetchInterval: number) {
  const apiReady = useApiAuthReady();
  return useQuery<WorkItems>({
    queryKey: ['agent-teams', teamId, 'work-items'],
    queryFn: async () => (await ApiService.getInstance().request('GET', `/agent-teams/${teamId}/work-items`)) as WorkItems,
    enabled: apiReady,
    refetchInterval,
  });
}

// A version's test copies (teams.test_copy) by id: they ran a step of a version being tested.
type WorkItems = { work_items: WorkItem[]; test_steps?: Record<string, { name: string; version: number }> };

// A step (Agent id) by name: a live step, or a version's test copy.
const agentNamer = (steps: Member[], tests: WorkItems['test_steps']) => (id: string) => {
  const test = tests?.[id];
  return steps.find((s) => s.id === id)?.name ?? (test ? `${test.name} · v${test.version} test` : 'removed step');
};
// Run ids are <stack>:<tenant>:def-<step id>:<event>.
const stepNamer = (steps: Member[], tests: WorkItems['test_steps']) => (run: string) =>
  agentNamer(steps, tests)(run.split(':')[2]?.replace(/^def-/, '') ?? '');

export default function AgentRuns({ teamId, steps, onWatch }: { teamId: string; steps: Member[]; onWatch: (workItemId: string) => void }) {
  const apiReady = useApiAuthReady();
  const items = useWorkItems(teamId, 15_000);
  const stepName = stepNamer(steps, items.data?.test_steps);
  const status = useRunStatuses(`/agent-teams/${teamId}/runs`, (items.data?.work_items ?? []).flatMap((w) => w.firings.at(-1)?.runs ?? []));

  if (items.isLoading || !apiReady) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const list = items.data?.work_items ?? [];
  return (
    <section className="space-y-3">
      <p className="text-sm text-muted-foreground">The 20 most recent pieces of work, and any older one waiting for approval, each with the steps that ran on it. Updates every 15 seconds.</p>
      {!list.length && <p className="text-sm text-muted-foreground">No runs yet.</p>}
      {list.map((w) => {
        const outcome = workOutcome(w, status);
        return (
        <Card key={w.id}>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium truncate">{w.title}</CardTitle>
            <OutcomeBadge outcome={outcome} />
            <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">{new Date(w.updated_at).toLocaleString()}</span>
            <Button size="sm" variant="outline" disabled={!w.runs.length && !w.failures.length} onClick={() => onWatch(w.id)}>
              {isDone(outcome) ? 'Logs' : 'Watch'}
            </Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Firings firings={w.firings} run={(r) => <Badge variant="secondary">{stepName(r)}</Badge>} />
            {w.links
              .filter((l) => !l.name.startsWith('source_')) // the event that started it, not something it made
              .map((l) => (
                <div key={`${l.name}-${l.key ?? ''}`} className="text-xs">
                  <span className="text-muted-foreground">{l.name}: </span>
                  {l.url ? (
                    <a href={l.url} target="_blank" rel="noreferrer" className="underline">
                      {l.title ?? l.url}
                    </a>
                  ) : (
                    l.title
                  )}
                </div>
              ))}
            <Failures failures={w.failures} />
            {w.approvals.map((a) => (
              <ApprovalLine key={a.id} a={a}>
                {a.status === 'pending' && (
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => onWatch(w.id)}>
                    Review
                  </Button>
                )}
              </ApprovalLine>
            ))}
          </CardContent>
        </Card>
        );
      })}
    </section>
  );
}

/** One piece of work, live: its steps' runs in order (following the newest until you pick one),
 * what waits for approval, and the chosen run as it goes. */
export function WorkItemSheet({ teamId, workItemId, steps, onClose }: {
  teamId: string;
  workItemId: string | null;
  steps: Member[];
  onClose: () => void;
}) {
  const items = useWorkItems(teamId, 3_000);
  const [picked, setPicked] = useState<string | null>(null);
  const item = items.data?.work_items.find((w) => w.id === workItemId);
  const run = picked ?? item?.runs.at(-1) ?? null;
  const stepName = stepNamer(steps, items.data?.test_steps);
  const agentName = agentNamer(steps, items.data?.test_steps);
  const base = `/agent-teams/${teamId}/runs`;
  const status = useRunStatuses(base, item?.firings.at(-1)?.runs ?? []);
  const close = () => {
    setPicked(null);
    onClose();
  };

  return (
    <Sheet open={!!workItemId} onOpenChange={(open) => !open && close()}>
      <SheetContent className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="truncate">{item?.title ?? 'Run'}</span>
            {item && <OutcomeBadge outcome={workOutcome(item, status)} />}
          </SheetTitle>
          <SheetDescription>Each step's run, as it goes. A hand-off adds the next step here.</SheetDescription>
        </SheetHeader>
        {!item ? (
          items.isLoading ? <Spinner /> : <p className="text-sm text-muted-foreground">This work isn't among the agent's 20 most recent.</p>
        ) : (
          <>
            {item.approvals.map((a) =>
              a.status === 'pending' ? <ApprovalPanel key={a.id} teamId={teamId} a={a} name={agentName} /> : <ApprovalLine key={a.id} a={a} />,
            )}
            <Firings
              firings={item.firings}
              run={(r) => (
                <Button size="sm" className="gap-1.5" variant={r === run ? 'secondary' : 'ghost'} onClick={() => setPicked(r)}>
                  <RunDot base={base} runId={r} />
                  {stepName(r)}
                </Button>
              )}
            />
            <Failures failures={item.failures} />
            {run ? (
              // Fills what's left of the sheet, but never less than this: with an approval open above it, the sheet scrolls instead.
              <div className="flex min-h-[28rem] flex-1 flex-col">
                <RunLive
                  key={run}
                  base={base}
                  runId={run}
                  speaker={stepName(run)}
                  steerPlaceholder="Direct this step (read at its next step)"
                  resume={{ path: 'resume' }}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No step has started on this yet.</p>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
